import { getReq } from "./request";
import { bestMatch } from "./fuzzyMatch";
import { countriesIn } from "./companyDetails";

/** A place as the lookups know it: a city with its country, or a country on its own. */
export interface LookedUpPlace {
    key: string;
    city: any | null;
    country: any | null;
    label: string;
    /** The name as written; false = the closest one, shown as predicted. */
    exact: boolean;
}

const plain = (value: string) => String(value ?? "").normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();

const countryNamed = async (name: string): Promise<any | null> => {
    const response = await getReq<any[]>(`lookups/countries?keyword=${encodeURIComponent(name)}`).catch(() => null);
    const rows: any[] = Array.isArray(response?.body) ? response!.body : [];
    const hit = bestMatch(name, rows.map((row) => String(row?.name ?? "")), 0.6);
    return hit ? rows.find((row) => row?.name === hit.value) ?? null : null;
};

/**
 * A place someone typed ("Muscat, Oman", "Doha", "Qatar", "UAE") as a city with
 * its country, or a country — looked up as the person, the closest name when
 * it isn't exact. Null when nothing close is found.
 * @param text - The place as written.
 */
export const lookUpPlace = async (text: string): Promise<LookedUpPlace | null> => {
    const written = String(text ?? "").trim();
    if (written.length < 2) return null;
    const [cityPart, ...rest] = written.split(/\s*,\s*/);
    const named = countriesIn(written)[0] ?? null;
    // just a country ("Qatar", "the UAE")
    if (named && plain(cityPart).length && countriesIn(cityPart)[0] === named && !rest.length) {
        const country = await countryNamed(named);
        return country ? { key: `country:${country.countryId}`, city: null, country, label: String(country.name), exact: true } : null;
    }
    const response = await getReq<any[]>(`lookups/cities?keyword=${encodeURIComponent(cityPart)}`).catch(() => null);
    let cities: any[] = Array.isArray(response?.body) ? response!.body.filter((city) => city?.cityId && city?.name) : [];
    // "Muscat, Oman": the city in that country
    if (named) cities = cities.filter((city) => !city.country?.name || bestMatch(named, [String(city.country.name)], 0.6));
    const exact = cities.filter((city) => plain(city.name) === plain(cityPart));
    const pool = exact.length ? exact : cities;
    const hit = pool.length === 1 ? { value: String(pool[0].name) } : bestMatch(cityPart, pool.map((city) => String(city.name)), 0.6);
    const city = hit ? pool.find((candidate) => String(candidate.name) === hit.value) : null;
    if (city) {
        const country = city.country ?? (named ? await countryNamed(named) : null);
        return { key: `city:${city.cityId}`, city, country, label: country?.name ? `${city.name}, ${country.name}` : String(city.name), exact: plain(city.name) === plain(cityPart) };
    }
    if (named) {
        const country = await countryNamed(named);
        return country ? { key: `country:${country.countryId}`, city: null, country, label: String(country.name), exact: false } : null;
    }
    return null;
};
